import { HandledError } from "common/errors";
import * as logger from "common/logger";
import { resolveScorecardConfig } from "components/reportsV3/utils/scorecardUtils";
import { buildDashboardReportConfig } from "components/reportsV3/utils/submissionStatusSpec";
import { endpoints } from "config";
import { authenticatedFetch, plainFetch } from "utils";

const COLUMN_TYPE_TO_DISPLAY_TYPE = {
  STRING: "text",
  ENUM: "option",
  DATE: "date",
  BOOL: "binary",
  FLOAT: "unit",
  INT: "unit",
};

export const getDisplayDataType = (type, subType) => {
  if (subType === "CURRENCY") {
    return "currency";
  }
  if (subType === "PERCENTAGE") {
    return "percentage";
  }
  if (subType === "YEAR") {
    return "year";
  }
  return COLUMN_TYPE_TO_DISPLAY_TYPE[type];
};

export const columnResponseToColumnDefinition = (column) => {
  return {
    ...column,
    dataType: getDisplayDataType(column.type, column.subType),
    displayType: "normal",
  };
};

export const LOG_PREFIX = "[Reports V3 API]";

export const NETWORK_ERROR_MESSAGE =
  "Couldn't reach the server. Check your connection and try again.";

// Pydantic validation failures arrive as a JSON list of { loc, msg }; turn
// them into "field: problem" text, or return the text unchanged.
export const formatValidationMessage = (text) => {
  try {
    const issues = JSON.parse(text);
    if (Array.isArray(issues) && issues.length > 0) {
      return issues
        .map(({ loc, msg }) =>
          Array.isArray(loc) && loc.length > 0
            ? `${loc.join(".")}: ${msg}`
            : msg
        )
        .join("; ");
    }
  } catch {
    // not JSON: fall through
  }
  return text;
};

// The text a user should see for a failed request. A HandledError from
// authenticatedFetch carries the backend's message (a ClientException message
// or validation text) on `description`; its `message` is only the error type,
// which is what used to reach the UI as "INTERNAL_SERVER_ERROR".
export const describeApiError = (error) => {
  if (error instanceof HandledError) {
    const text = error.description || error.title || error.name;
    return error.name === "VALIDATION_ERROR"
      ? formatValidationMessage(text)
      : text;
  }
  if (
    error instanceof TypeError &&
    /fetch|network|load failed/i.test(error.message)
  ) {
    return NETWORK_ERROR_MESSAGE;
  }
  if (error instanceof Error) {
    return error.message || String(error);
  }
  return String(error);
};

export const createApiError = (operation, error, logLevel = "error") => {
  const breadcrumb = `${LOG_PREFIX} ${operation}`;
  if (logLevel === "warn") {
    logger.info(breadcrumb, error);
  } else {
    logger.error(error, { breadcrumb });
  }
  return {
    error: {
      status: error?.status ?? 500,
      data: describeApiError(error),
    },
  };
};

export async function withApiErrorHandling(operation, fn, logLevel = "error") {
  try {
    return await fn();
  } catch (error) {
    return createApiError(operation, error, logLevel);
  }
}

export const reportsV3BaseQuery = async ({
  path,
  method = "GET",
  body,
  headers,
  parseAs = "json",
  expectOk = false,
  notFoundFallback,
  operation = "Failed request",
  logLevel = "error",
}) =>
  withApiErrorHandling(
    operation,
    async () => {
      const requestOptions = {
        method,
      };
      if (body !== undefined) {
        requestOptions.headers = {
          "Content-Type": "application/json",
          ...headers,
        };
        requestOptions.body = JSON.stringify(body);
      } else if (headers) {
        requestOptions.headers = headers;
      }
      const response = await authenticatedFetch(
        endpoints.reportsV3(path),
        requestOptions
      );
      if (notFoundFallback !== undefined && response.status === 404) {
        return {
          data: notFoundFallback,
        };
      }
      if (expectOk && !response.ok) {
        throw new Error(`HTTP ${response.status}: ${operation}`);
      }
      if (parseAs === "none") {
        return {
          data: null,
        };
      }
      const data = await response.json();
      return {
        data,
      };
    },
    logLevel
  );

export const POLL_INTERVAL_MS = 500;
export const MAX_POLL_ATTEMPTS = 300;

export async function extractReadyData(response, outputType = "JSON") {
  const presentation = response.responsePresentation;
  const isJsonResponse = outputType === "JSON" || outputType === "SUMMARISING";
  if (!isJsonResponse) {
    if (presentation === "BY_URL" && response.dataUrl) {
      return { data: { url: response.dataUrl } };
    }
    return { data: { data: response.data } };
  }
  if (presentation === "BY_URL" && response.dataUrl) {
    try {
      const urlResponse = await plainFetch(response.dataUrl);
      return { data: await urlResponse.json() };
    } catch (err) {
      return {
        error: {
          status: 500,
          data: `Failed to fetch report data from URL: ${err.message ?? err}`,
        },
      };
    }
  }
  return { data: response.data };
}

// A run that ended in anything but READY. A FAILED run carries the reason in
// `errorMsg`; show that rather than the bare status.
const runStatusError = (operationLabel, data) => ({
  error: {
    status: 500,
    data:
      data?.status === "FAILED" && data.errorMsg
        ? data.errorMsg
        : `${operationLabel}: unexpected status "${data?.status}"`,
  },
});

export async function pollUntilReady(
  fetchFn,
  operationLabel,
  outputType = "JSON"
) {
  const firstResult = await fetchFn();
  if (firstResult.error) return firstResult;
  const firstData = firstResult.data;
  if (firstData?.status === "READY") {
    return extractReadyData(firstData, outputType);
  }
  if (firstData?.status !== "IN_PROGRESS" || !firstData.statusId) {
    return runStatusError(operationLabel, firstData);
  }
  const { reportId, statusId } = firstData;
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    const pollResult = await reportsV3BaseQuery({
      path: `reports/${reportId}/status/${statusId}`,
      method: "GET",
      parseAs: "json",
      operation: `Poll status for ${operationLabel}`,
    });
    if (pollResult.error) return pollResult;
    if (pollResult.data?.status === "READY") {
      return extractReadyData(pollResult.data, outputType);
    }
    if (pollResult.data?.status !== "IN_PROGRESS") {
      return runStatusError(operationLabel, pollResult.data);
    }
  }
  return {
    error: {
      status: 408,
      data: `Timed out waiting for ${operationLabel} to complete`,
    },
  };
}

// "INLINE" runs the report inside the web request and only hands it to the
// background if it is still running after the backgrounding threshold.
// "BY_URL" backgrounds the run immediately and returns a status id, which
// pollUntilReady resolves into a data URL.
export const buildRunRequest = ({
  outputType = "JSON",
  presentationType = "INLINE",
  limitRows,
  skipRows,
  asAt,
} = {}) => ({
  type: outputType,
  presentationType,
  ...(limitRows != null && { limitRows }),
  ...(skipRows != null && { skipRows }),
  ...(asAt ? { asAt } : {}),
});

export const fetchSubmissionReviewConfig = async () => {
  const response = await authenticatedFetch(
    endpoints.staticData("retrieve-config"),
    {
      headers: { "Content-Type": "application/json" },
      method: "post",
      body: JSON.stringify({ keys: ["submission_review_config"] }),
    }
  );
  const data = await response.json();
  return data?.submission_review_config ?? null;
};

export const buildDashboardReport = async () => {
  const [config, columnsResult] = await Promise.all([
    fetchSubmissionReviewConfig(),
    reportsV3BaseQuery({
      path: "tables/review_list",
      operation: "Failed to fetch review_list columns",
    }),
  ]);
  const knownColumnNames = new Set(
    columnsResult.data.columns.map((c) => c.internalName)
  );
  // Map of internalName -> backend display name (e.g. "industryClass" ->
  // "Industry Class", "bi" -> "BI") so the dashboard shows human-readable
  // headers instead of raw column keys.
  const displayNameMap = Object.fromEntries(
    columnsResult.data.columns
      .filter((c) => c.internalName && c.displayName)
      .map((c) => [c.internalName, c.displayName])
  );
  const report = buildDashboardReportConfig(
    config,
    knownColumnNames,
    displayNameMap
  );
  // The Review scorecard (ReviewScorecard.js) is configured by the same
  // document; resolve it here, against the table's real columns, so the panel
  // neither re-fetches the config nor depends on which columns the user's view
  // shows. mergeSavedLayout keeps `configuration`, so it survives a saved view.
  report.configuration = {
    ...report.configuration,
    scorecard: resolveScorecardConfig(config, knownColumnNames),
    columnDisplayNames: displayNameMap,
  };
  // Return the full known-column set too, so mergeSavedLayout can tell a genuinely
  // removed column apart from one that's simply not in the company default view.
  return { report, knownColumns: knownColumnNames };
};
